import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import TopStrip from "../components/TopStrip";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import HelpButton from "../components/HelpButton";

import heroBanner from "../assets/images/hero-images/whitelabel.webp";
import heroMobile from "../assets/images/mobile-banners/whitelabel-mobile.webp";
import downloadImg from "../assets/images/webp-images/download-section-img.webp";
import companyPdf from "../assets/pdfs/DDevOps-Company-Profile.pdf";

import invisiblePartner from "../assets/images/whitelabel-page/invisible-partner.png";
import wholesalePricing from "../assets/images/whitelabel-page/wholesale-pricing.png";
import support247 from "../assets/images/whitelabel-page/24_7.png";
import elasticImg from "../assets/images/whitelabel-page/Elastic-establity.png";
import dedicatedAccount from "../assets/images/whitelabel-page/dedicated-account.png";
import riskFree from "../assets/images/whitelabel-page/risk-free.png";

import logo1 from "../assets/images/logo-slide/1.avif";
import logo2 from "../assets/images/logo-slide/2.avif";
import logo3 from "../assets/images/logo-slide/3.avif";
import logo4 from "../assets/images/logo-slide/4.avif";
import logo5 from "../assets/images/logo-slide/5.avif";
import logo6 from "../assets/images/logo-slide/6.avif";
import logo7 from "../assets/images/logo-slide/7.avif";
import logo8 from "../assets/images/logo-slide/8.avif";
import logo9 from "../assets/images/logo-slide/9.avif";

const logoImages = [
  logo1,
  logo2,
  logo3,
  logo4,
  logo5,
  logo6,
  logo7,
  logo8,
  logo9,
];

// zigzag blocks — exact text from original HTML
const pillars = [
  {
    num: "01",
    reverse: false,
    img: invisiblePartner,
    alt: "invisible-partner vector",
    title: "The Invisible Partner",
    desc: "We stay in the shadows. We work strictly white-label, meaning we use your email domains, your ticketing systems, and your branding. To your client, we are just your expert internal technical team.",
  },
  {
    num: "02",
    reverse: true,
    img: wholesalePricing,
    alt: "Wholesale Pricing Model Vector",
    title: "Wholesale Pricing Model",
    desc: 'Maximize your margins. We offer our partners significantly discounted "wholesale" rates on managed hosting and DevOps hours. You control the final pricing for your client, keeping 100% of the markup.',
  },
  {
    num: "03",
    reverse: false,
    img: support247,
    alt: "24/7 White-Label Support Vector",
    title: "24/7 White-Label Support",
    desc: "Sleep soundly while we work. Our team monitors your clients' servers 24/7/365. If an issue arises at 3 AM, we fix it immediately—your client stays happy, and you don't even wake up.",
  },
  {
    num: "04",
    reverse: true,
    img: elasticImg,
    alt: "Elastic Scalability Vector",
    title: "Elastic Scalability",
    desc: 'Never turn down a project because it\'s "too big." Whether your client needs a simple web server or a multi-region Kubernetes cluster, we have the infrastructure and skills to build and manage it.',
  },
  {
    num: "05",
    reverse: false,
    img: dedicatedAccount,
    alt: "Dedicated Account Management Vector",
    title: "Dedicated Account Management",
    desc: "You won't be talking to a chatbot. You get a dedicated technical account manager who understands your agency's business model and helps you prepare quotes and architecture diagrams for your sales pitches.",
  },
  {
    num: "06",
    reverse: true,
    img: riskFree,
    alt: "Risk-Free Execution Vector",
    title: "Risk-Free Execution",
    desc: "Don't gamble your agency's reputation on freelancers. We guarantee our SLAs. Our standardized, automated workflows ensure that every project you deliver is secure, fast, and bug-free.",
  },
];

const sellableServices = [
  {
    title: "Managed Cloud Hosting",
    desc: "Resell premium managed hosting on AWS, DigitalOcean, or Vultr. We handle the setup, security, and updates; you invoice the client for a recurring monthly fee.",
  },
  {
    title: "DevOps-as-a-Service",
    desc: "Offer CI/CD pipeline setup and automation services. Help your clients release code faster while we build the pipelines behind the scenes.",
  },
  {
    title: "Security & Compliance Audits",
    desc: "Sell high-value security packages. We perform penetration testing, server hardening, and compliance checks (GDPR/HIPAA) under your brand.",
  },
  {
    title: "24/7 Server Monitoring",
    desc: "Create a 'Proactive Maintenance' plan. We install our monitoring agents to track uptime and performance, fixing issues before the client even notices.",
  },
  {
    title: "Application Migration",
    desc: "Charge for complex migrations. We handle the heavy lifting of moving legacy apps to the cloud with zero data loss, ensuring a smooth transition.",
  },
  {
    title: "Disaster Recovery Planning",
    desc: "Sell peace of mind. We design and implement robust backup and disaster recovery strategies, ensuring your clients' data is safe no matter what happens.",
  },
];

function WhitelabelSolution() {
  const logoTrackRef = useRef(null);
  const animRef = useRef(null);
  const posRef = useRef(0);

  useEffect(() => {
    const track = logoTrackRef.current;
    if (!track) return;
    const run = () => {
      posRef.current -= 0.5;
      if (Math.abs(posRef.current) >= track.scrollWidth / 2) posRef.current = 0;
      track.style.left = posRef.current + "px";
      animRef.current = requestAnimationFrame(run);
    };
    animRef.current = requestAnimationFrame(run);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  return (
    <>
      <TopStrip />
      <Navbar />

      {/* HERO */}
      <section style={{ marginTop: "125px" }}>
        <div className="container-fluid p-0">
          <img
            src={heroBanner}
            width="1600"
            height="520"
            alt="Whitelabel engineering banner with 3D mechanical engine and enterprise-grade branding tagline."
            className="img-fluid w-100 d-none d-md-block"
          />
          <img
            src={heroMobile}
            width="768"
            height="1000"
            alt="Whitelabel engineering banner with 3D mechanical engine and enterprise-grade branding tagline."
            className="img-fluid w-100 d-md-none d-block"
          />
        </div>
      </section>

      {/* INTRO */}
      <section className="my-5">
        <div className="container">
          <div className="container py-4">
            <h2
              className="text-center top-line fw-bold text-uppercase"
              data-aos="fade-down"
            >
              Whitelabel Solution for{" "}
              <span className="text-info">Information Technology</span>
            </h2>
          </div>
          <div className="row g-4">
            {/* Left */}
            <div className="col-lg-6 col-md-6 col-sm-12 d-flex align-items-start justify-content-center flex-column">
              <h3
                className="text-md-start text-center ps-0 fs-6 fs-md-5"
                data-aos="fade-up"
              >
                DDevOps specializes in managed IT services such as IT
                operations, system engineering, server management, cloud
                computing, infrastructure & network management, security
                assistance, etc.
              </h3>
              <div className="d-flex flex-wrap justify-content-md-start justify-content-center gap-3 mt-3">
                <Link
                  to="/contact"
                  className="btn btn-info text-white fw-bold"
                  data-aos="fade-right"
                >
                  Talk to a consultant
                </Link>
                <Link
                  to="/contact"
                  className="btn btn-info text-white fw-bold"
                  data-aos="fade-left"
                >
                  Contact us now
                </Link>
              </div>
            </div>
            {/* Right */}
            <div className="col-lg-6 col-md-6 col-sm-12 text-md-start text-center border-start border-3 border-info">
              <p data-aos="fade-up">
                We help organizations implement and optimize their technical
                systems. Our white-label program typically takes place between
                technology vendors who provide IT products and the agencies and
                companies who want to enhance their daily operations
                capabilities through our skills employed to deliver the service
                (design, build, run) from your own branding. Technically, these
                fall into three main categories: business process services,
                application services, and infrastructure services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SCALE YOUR BUSINESS */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center mb-5">
            <div className="col-lg-10 text-center">
              <h2 className="fw-bold mb-3 text-uppercase">
                Scale Your <span className="text-info">Business</span>, Not Your{" "}
                <span className="text-info">Overhead</span>
              </h2>
              <p className="text-muted fs-5 mb-0">
                Stop saying "No" to complex technical projects. With DDevOps as
                your invisible backend partner, you can bid on enterprise
                contracts without hiring a single new engineer.
              </p>
            </div>
          </div>
          <div className="row g-4">
            <div className="col-md-6 col-lg-4">
              <div className="h-100 bg-white p-4 rounded-4 shadow-sm border">
                <h5 className="fw-semibold mb-2">Instant Expertise</h5>
                <p className="text-dark mb-2">
                  Add a 40+ person DevOps team to your agency overnight.
                </p>
                <p className="text-muted small mb-0">
                  Gain immediate access to certified engineers without
                  recruitment, onboarding, or long-term staffing commitments.
                </p>
              </div>
            </div>
            <div className="col-md-6 col-lg-4">
              <div className="h-100 bg-white p-4 rounded-4 shadow-sm border">
                <h5 className="fw-semibold mb-2">Pure Profit</h5>
                <p className="text-dark mb-2">
                  Access exclusive wholesale rates and set your own margins.
                </p>
                <p className="text-muted small mb-0">
                  Maintain full pricing control while increasing revenue across
                  complex infrastructure and enterprise projects.
                </p>
              </div>
            </div>
            <div className="col-md-6 col-lg-4">
              <div className="h-100 bg-white p-4 rounded-4 shadow-sm border">
                <h5 className="fw-semibold mb-2">100% Confidential</h5>
                <p className="text-dark mb-2">
                  We work strictly under NDA. Your clients never know we exist.
                </p>
                <p className="text-muted small mb-0">
                  All delivery is handled invisibly under your brand, preserving
                  trust and long-term client relationships.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHITELABEL FRAMEWORKS */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="mb-5">
            <h2 className="fw-bold text-uppercase">
              Our Whitelabel <span className="text-info">Frameworks</span>
            </h2>
            <p className="text-muted mt-2" style={{ maxWidth: "720px" }}>
              Clearly defined collaboration models designed for agencies and IT
              providers looking to scale without expanding internal teams.
            </p>
          </div>

          {/* TYPE 1 */}
          <div className="row align-items-center mb-5">
            <div className="col-lg-3 mb-3 mb-lg-0">
              <div className="p-3 border-start border-4 border-info bg-light h-100">
                <h5 className="fw-bold mb-1">TYPE 1</h5>
                <p className="mb-0 text-muted">
                  Digital &amp; Creative Agencies
                </p>
              </div>
            </div>
            <div className="col-lg-9">
              <div className="p-4 border rounded h-100">
                <p className="mb-2">
                  <strong>Who this is for:</strong>
                  <br />
                  Marketing agencies, Web Design firms, and SEO companies.
                </p>
                <p className="mb-0">
                  <strong>How we collaborate:</strong>
                  <br />
                  You focus on the creative vision, UI/UX, and client
                  relationships. We act as your "Backend Engineering
                  Department," handling the server setup, CMS customization, and
                  deployment pipelines invisibly.
                </p>
              </div>
            </div>
          </div>

          {/* TYPE 2 */}
          <div className="row align-items-center">
            <div className="col-lg-3 mb-3 mb-lg-0">
              <div className="p-3 border-start border-4 border-info bg-light h-100">
                <h5 className="fw-bold mb-1">TYPE 2</h5>
                <p className="mb-0 text-muted">IT Consultancies &amp; MSPs</p>
              </div>
            </div>
            <div className="col-lg-9">
              <div className="p-4 border rounded h-100">
                <p className="mb-2">
                  <strong>Who this is for:</strong>
                  <br />
                  Managed Service Providers and IT consultants facing capacity
                  limits.
                </p>
                <p className="mb-0">
                  <strong>How we collaborate:</strong>
                  <br />
                  DDevOps provides "Overflow Support" and specialized expertise
                  (like Kubernetes or Cloud Migration) that your team might
                  lack. We work within your ticketing systems and use your email
                  domains to support your end clients.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PATH TO PROFITABILITY */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold text-dark text-uppercase">
              Path to <span className="text-info">Profitability</span>
            </h2>
          </div>
          <div className="row g-4">
            {[
              {
                num: "01",
                title: "Alignment & NDA",
                desc: "Security first. We sign a strict Non-Disclosure Agreement (NDA) and define the Wholesale Rate Card, ensuring your margins are protected before we see a single byte of data.",
              },
              {
                num: "02",
                title: "Process Integration",
                desc: "We become you. We set up white-labeled communication channels (using your agency's email domain), integrate with your Slack/Jira, and align with your project management workflows.",
              },
              {
                num: "03",
                title: "Invisible Delivery",
                desc: "We execute. Our teams build, deploy, and manage the infrastructure. Your client receives high-quality deliverables under your brand, and you receive the credit.",
              },
            ].map((s, i) => (
              <div className="col-lg-4" key={i}>
                <div className="h-100 bg-white border border-info-subtle rounded-4 p-4">
                  <span className="badge bg-info mb-3">{s.num}</span>
                  <h5 className="fw-bold mb-3 text-dark">{s.title}</h5>
                  <p className="mb-0 text-muted">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPERATIONAL PILLARS — zigzag */}
      <section className="py-5 px-3 bg-light">
        <div className="container">
          <h2 className="text-center mb-5 text-uppercase fw-bold top-line">
            Operational Pillars of Our{" "}
            <span className="text-info">Whitelabel Partnership</span>
          </h2>

          {pillars.map((b, i) => (
            <div
              className={`row align-items-center mb-5 ${b.reverse ? "flex-lg-row-reverse" : ""}`}
              key={i}
            >
              <div className="col-lg-6 mb-4 mb-lg-0">
                <div className="p-4 p-lg-5 bg-white rounded shadow-sm border-start border-info border-3 position-relative">
                  <div
                    className="position-absolute top-0 start-0 translate-middle bg-info text-white rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: "40px", height: "40px" }}
                  >
                    {b.num}
                  </div>
                  <h5 className="fw-bold mt-3">{b.title}</h5>
                  <p className="text-muted mb-0">{b.desc}</p>
                </div>
              </div>
              <div className="col-lg-6 text-center">
                <img src={b.img} className="img-fluid" alt={b.alt} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES YOU CAN SELL TODAY */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold top-line">Services You Can Sell Today</h2>
            <p className="text-muted fs-5">
              A complete catalog of high-demand technical services, ready for
              your label.
            </p>
          </div>
          <div className="row g-4">
            {sellableServices.map((s, i) => (
              <div className="col-md-6 col-lg-4" key={i}>
                <div className="p-4 rounded shadow-sm bg-white h-100">
                  <h5 className="text-info fw-bold">{s.title}</h5>
                  <p className="text-muted mb-0">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER RESOURCES / DOWNLOAD */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="text-center mb-5">
            <h4 className="fw-semibold mb-2 top-line">
              Ready to expand your agency capabilities?
            </h4>
            <Link to="/contact" className="fs-5 fw-bold">
              Join the partner ecosystem!
            </Link>
          </div>
          <div
            className="row align-items-center gy-4"
            style={{ marginTop: "5rem" }}
          >
            <div
              className="col-lg-6 col-md-12 text-center text-lg-start"
              data-aos="fade-left"
              data-aos-delay="300"
            >
              <div className="px-3 px-lg-5">
                <p className="text-dark opacity-75 fs-4 mb-2">
                  AGENCY RESOURCES
                </p>
                <h2 className="fw-bold mb-3">
                  <span className="txt-color">Get The</span>
                  <br />
                  Partner Starter Kit
                </h2>
                <p className="text-muted fs-5 mb-3">
                  Ready to see the numbers? Download our Whitelabel Partner
                  Guide, which includes our Wholesale Rate Card, Service Level
                  Agreements (SLAs), and sample white-label contracts.
                </p>
                <a
                  className="btn btn-info text-white fw-bold px-4 py-3 mt-2 d-inline-block"
                  target="_blank"
                  rel="noreferrer"
                  href={companyPdf}
                >
                  Download Partner Kit (PDF)
                </a>
              </div>
            </div>
            <div
              className="col-lg-6 col-md-12 text-center"
              data-aos="fade-right"
              data-aos-delay="300"
            >
              <img
                src={downloadImg}
                loading="lazy"
                alt="Partner Starter Kit"
                className="img-fluid floating-icon mt-4"
                style={{ maxHeight: "350px", objectFit: "contain" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* DARK CTA */}
      <section className="py-5 bg-dark text-white">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <h2 className="fw-bold mb-2">Stop leaving money on the table.</h2>
              <p className="text-light mb-0 fs-5">
                Start selling high-margin DevOps services today.
              </p>
            </div>
            <div className="col-lg-4 text-lg-end mt-4 mt-lg-0">
              <Link to="/contact" className="btn btn-outline-light btn-lg px-4">
                Apply for Partnership
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* LOGO SLIDER */}
      <section className="logo-slider py-1 bg-white overflow-hidden mt-5">
        <h2 className="text-center text-uppercase top-line">
          The <span className="text-info fw-semibold">DDevOps</span> Toolkit
        </h2>
        <div className="container" style={{ margin: "1rem auto" }}>
          <div className="logo-wrapper">
            <div
              className="logo-track"
              ref={logoTrackRef}
              style={{ position: "relative" }}
            >
              {[...logoImages, ...logoImages].map((img, i) => (
                <img
                  key={i}
                  src={img}
                  loading="lazy"
                  className="slide-logo-img"
                  alt={`Logo ${(i % 9) + 1}`}
                  height="60"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <HelpButton />
    </>
  );
}

export default WhitelabelSolution;
